/**
 * assets/homepage.js
 * Homepage interactivity: hero auto-cycle + preset playground chip sync.
 * Loaded globally as type="module" from theme.liquid.
 * No-op on non-homepage pages (guards on #home-hero-belt).
 */

import { PRESETS, CATEGORIES } from 'nion-fx';

// ── Element refs ──────────────────────────────────────────────────────────
const heroBelt        = document.getElementById('home-hero-belt');
const heroBadge       = document.getElementById('home-hero-badge');
const heroDotsCtn     = document.getElementById('home-hero-dots');
const seeItBtn        = document.getElementById('home-see-it-btn');
const playgroundBelt  = document.getElementById('home-playground-belt');
const playgroundChips = document.querySelector('.home-playground-chips');
const counterEl       = document.getElementById('home-playground-counter');
const nameEl          = document.getElementById('home-playground-name');
const familyEl        = document.getElementById('home-playground-family');
const swatchesEl      = document.getElementById('home-playground-swatches');

// Guard: no-op on non-homepage pages
if (heroBelt) { init(); }

function init() {

  // ── 1. Hero dot picker (8 Signature looks, matching the auto-cycle window)
  const HERO_PRESETS = PRESETS.filter(p => p.family === 'Signature').slice(0, 8);

  HERO_PRESETS.forEach((preset, i) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'home-hero-dot' + (i === 0 ? ' home-hero-dot--active' : '');
    dot.setAttribute('aria-label', preset.name);
    dot.style.background = i === 0 ? preset.swatch[0] : 'var(--ink-5)';
    if (i === 0) dot.style.boxShadow = `0 0 10px ${preset.swatch[0]}`;
    dot.addEventListener('click', () => {
      clearInterval(autoTimer);
      heroBelt.setPreset(preset.id);
    });
    heroDotsCtn.appendChild(dot);
  });

  function syncHeroDots(activeIndex) {
    document.querySelectorAll('.home-hero-dot').forEach((dot, i) => {
      const on = i === activeIndex;
      dot.classList.toggle('home-hero-dot--active', on);
      dot.style.background = on ? HERO_PRESETS[i].swatch[0] : 'var(--ink-5)';
      dot.style.boxShadow  = on ? `0 0 10px ${HERO_PRESETS[i].swatch[0]}` : 'none';
    });
  }

  // ── 2. Hero auto-cycle capped to 8 — so an active dot is always visible
  let autoIndex = 0;
  let autoTimer = setInterval(() => {
    autoIndex = (autoIndex + 1) % HERO_PRESETS.length;
    heroBelt.setPreset(HERO_PRESETS[autoIndex].id);
  }, 5000);

  // ── 3. Hero preset-change: sync badge + dots ──────────────────────────
  heroBelt.addEventListener('preset-change', (e) => {
    const { preset } = e.detail;
    if (heroBadge) heroBadge.textContent = '\u25b8 ' + preset.name.toUpperCase();
    syncHeroDots(HERO_PRESETS.findIndex(p => p.id === preset.id));
  });

  // ── 4. "See it animate" scrolls to playground ─────────────────────────
  seeItBtn?.addEventListener('click', () => {
    document.getElementById('presets-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // ── 5. Playground (independent — no shared state with hero) ───────────
  if (!playgroundBelt || !playgroundChips) return;

  // Category tabs (same groups as the app's Presets tab), each showing its looks
  const tabsEl = document.createElement('div');
  tabsEl.className = 'home-playground-tabs';
  tabsEl.setAttribute('role', 'tablist');
  tabsEl.setAttribute('aria-label', 'Preset categories');
  playgroundChips.before(tabsEl);

  CATEGORIES.forEach(cat => {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'home-tab';
    tab.dataset.cat = cat;
    tab.setAttribute('role', 'tab');
    tab.textContent = cat;
    tab.addEventListener('click', () => showCategory(cat));
    tabsEl.appendChild(tab);
  });

  function showCategory(cat) {
    tabsEl.querySelectorAll('.home-tab').forEach(t => {
      const on = t.dataset.cat === cat;
      t.classList.toggle('home-tab--active', on);
      t.setAttribute('aria-selected', String(on));
    });
    playgroundChips.querySelectorAll('.home-chip').forEach(c => {
      c.hidden = c.dataset.cat !== cat;
    });
  }

  PRESETS.forEach((preset, i) => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'home-chip' + (i === 0 ? ' home-chip--active' : '');
    chip.dataset.id = preset.id;
    chip.dataset.cat = preset.family;
    chip.setAttribute('aria-label', preset.name);

    const swatchRow = document.createElement('div');
    swatchRow.className = 'home-chip__swatches';
    preset.swatch.forEach(color => {
      const dot = document.createElement('span');
      dot.className = 'home-chip__dot';
      dot.style.background = color;
      dot.style.boxShadow  = `0 0 8px ${color}aa`;
      swatchRow.appendChild(dot);
    });

    const nameSpan = document.createElement('span');
    nameSpan.className = 'home-chip__name';
    nameSpan.textContent = preset.name;

    chip.appendChild(swatchRow);
    chip.appendChild(nameSpan);
    chip.addEventListener('click', () => playgroundBelt.setPreset(preset.id));
    playgroundChips.appendChild(chip);
  });

  showCategory(PRESETS[0].family);

  // Set initial playground UI from PRESETS[0]
  syncPlayground(0, PRESETS[0]);

  // ── 6. Playground preset-change: sync counter + meta + active chip ────
  playgroundBelt.addEventListener('preset-change', (e) => {
    const { id, index, preset } = e.detail;
    syncPlayground(index, preset);
    document.querySelectorAll('.home-chip').forEach(c => {
      c.classList.toggle('home-chip--active', c.dataset.id === id);
    });
  });

  function syncPlayground(index, preset) {
    if (counterEl) {
      counterEl.textContent =
        String(index + 1).padStart(2, '0') + ' / ' + String(PRESETS.length).padStart(2, '0');
    }
    if (nameEl)   nameEl.textContent   = preset.name;
    if (familyEl) familyEl.textContent = preset.family;
    if (swatchesEl) {
      // Use replaceChildren() — safe DOM method, no innerHTML
      swatchesEl.replaceChildren(
        ...preset.swatch.map(color => {
          const sq = document.createElement('span');
          sq.className = 'preset-playground__swatch';
          sq.style.background = color;
          sq.style.boxShadow  = `0 0 12px ${color}66`;
          return sq;
        })
      );
    }
  }

} // end init()
